import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Users,
  Search,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Phone,
  Mail,
  Globe,
  MessageCircle,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Copy,
  ExternalLink,
  Shield,
  ShieldCheck
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMarketers({ token }) {
  const { t } = useTranslation();
  const [marketers, setMarketers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMarketer, setSelectedMarketer] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [marketerDetails, setMarketerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchMarketers();
  }, []);

  const fetchMarketers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/referral/admin/marketers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMarketers(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load marketers");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketerDetails = async (marketerId) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API}/referral/admin/marketers/${marketerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMarketerDetails(response.data);
    } catch (error) {
      toast.error("Failed to load marketer details");
      console.error(error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetails = (marketer) => {
    setSelectedMarketer(marketer);
    setDetailsOpen(true);
    fetchMarketerDetails(marketer.user_id);
  };

  const handleVerifyMarketer = async (marketerId, isVerified) => {
    try {
      await axios.put(
        `${API}/referral/admin/marketers/${marketerId}?is_verified=${isVerified}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isVerified ? "Marketer verified" : "Verification removed");
      fetchMarketers();
    } catch (error) {
      toast.error("Failed to update marketer");
    }
  };

  const handleToggleActive = async (marketerId, isActive) => {
    try {
      await axios.put(
        `${API}/referral/admin/marketers/${marketerId}?is_active=${isActive}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isActive ? "Marketer activated" : "Marketer deactivated");
      fetchMarketers();
    } catch (error) {
      toast.error("Failed to update marketer");
    }
  };

  const handleSuspendAccount = async (marketerId, marketerName) => {
    const reason = window.prompt(`Enter reason for suspending ${marketerName}:`);
    if (!reason) return;
    
    try {
      await axios.post(
        `${API}/referral/admin/suspend-account/${marketerId}?reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Account suspended. Support ticket created automatically.");
      fetchMarketers();
    } catch (error) {
      toast.error("Failed to suspend account");
    }
  };

  const handleUnsuspendAccount = async (marketerId) => {
    try {
      await axios.post(
        `${API}/referral/admin/unsuspend-account/${marketerId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Account unsuspended");
      fetchMarketers();
    } catch (error) {
      toast.error("Failed to unsuspend account");
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/referral/admin/marketers/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'marketers_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const filteredMarketers = marketers.filter(m => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      m.account_name?.toLowerCase().includes(search) ||
      m.account_email?.toLowerCase().includes(search) ||
      m.full_name?.toLowerCase().includes(search) ||
      m.phone_number?.includes(search) ||
      m.referral_code?.toLowerCase().includes(search)
    );
  });

  const SocialIcon = ({ platform, value }) => {
    if (!value) return null;
    
    const icons = {
      telegram: MessageCircle,
      whatsapp: Phone,
      facebook: Globe,
      instagram: Globe,
      youtube: Globe,
      twitter: Globe,
      linkedin: Globe,
      tiktok: Globe,
      website: Globe
    };
    
    const Icon = icons[platform] || Globe;
    
    return (
      <a 
        href={value.startsWith('http') ? value : `https://${value}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-1.5 bg-gray-700 rounded-full hover:bg-gray-600 transition-colors"
        title={platform}
      >
        <Icon className="w-3.5 h-3.5 text-gray-300" />
      </a>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            {t('admin.marketers', 'Affiliate Marketers')}
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {t('admin.marketersDesc', 'Manage and view all affiliate marketers')}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMarketers}
            className="border-gray-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="border-gray-600"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder={t('admin.searchMarketers', 'Search by name, email, phone, or code...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-gray-800 border-gray-700 text-white"
        />
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            Total Marketers
          </div>
          <div className="text-2xl font-bold text-white">{marketers.length}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <ShieldCheck className="w-4 h-4 text-green-400" />
            Verified
          </div>
          <div className="text-2xl font-bold text-green-400">
            {marketers.filter(m => m.is_verified).length}
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            Total Referrals
          </div>
          <div className="text-2xl font-bold text-cyan-400">
            {marketers.reduce((sum, m) => sum + (m.stats?.total_referrals || 0), 0)}
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <DollarSign className="w-4 h-4 text-yellow-400" />
            Total Earnings
          </div>
          <div className="text-2xl font-bold text-yellow-400">
            ${marketers.reduce((sum, m) => sum + (m.stats?.total_earnings_usdt || 0), 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Marketers Table */}
      <div className="bg-gray-800/30 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr className="text-left text-gray-400 text-sm">
                <th className="px-4 py-3">Marketer</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Social</th>
                <th className="px-4 py-3">Stats</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading marketers...
                  </td>
                </tr>
              ) : filteredMarketers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                    No marketers found
                  </td>
                </tr>
              ) : (
                filteredMarketers.map((marketer) => (
                  <tr key={marketer.id} className="hover:bg-gray-800/30">
                    {/* Marketer Info */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                          {(marketer.full_name || marketer.account_name || 'M')[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-white flex items-center gap-2">
                            {marketer.full_name || marketer.account_name || 'Unknown'}
                            {marketer.is_verified && (
                              <ShieldCheck className="w-4 h-4 text-green-400" />
                            )}
                          </div>
                          <div className="text-sm text-gray-400 flex items-center gap-1">
                            <span className="font-mono bg-gray-700 px-1.5 py-0.5 rounded text-xs">
                              {marketer.referral_code}
                            </span>
                            <button onClick={() => copyToClipboard(marketer.referral_code)}>
                              <Copy className="w-3 h-3 text-gray-500 hover:text-gray-300" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="px-4 py-3">
                      <div className="space-y-1 text-sm">
                        {marketer.account_email && (
                          <div className="flex items-center gap-1 text-gray-300">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[150px]">{marketer.account_email}</span>
                          </div>
                        )}
                        {marketer.phone_number && (
                          <div className="flex items-center gap-1 text-gray-300">
                            <Phone className="w-3 h-3" />
                            <span>{marketer.phone_number}</span>
                          </div>
                        )}
                        {!marketer.account_email && !marketer.phone_number && (
                          <span className="text-gray-500">No contact info</span>
                        )}
                      </div>
                    </td>

                    {/* Social */}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {marketer.social_media?.telegram && (
                          <SocialIcon platform="telegram" value={`https://t.me/${marketer.social_media.telegram}`} />
                        )}
                        {marketer.social_media?.whatsapp && (
                          <SocialIcon platform="whatsapp" value={`https://wa.me/${marketer.social_media.whatsapp}`} />
                        )}
                        {marketer.social_media?.facebook && (
                          <SocialIcon platform="facebook" value={marketer.social_media.facebook} />
                        )}
                        {marketer.social_media?.instagram && (
                          <SocialIcon platform="instagram" value={marketer.social_media.instagram} />
                        )}
                        {marketer.social_media?.youtube && (
                          <SocialIcon platform="youtube" value={marketer.social_media.youtube} />
                        )}
                        {!marketer.social_media?.telegram && 
                         !marketer.social_media?.whatsapp && 
                         !marketer.social_media?.facebook && 
                         !marketer.social_media?.instagram && 
                         !marketer.social_media?.youtube && (
                          <span className="text-gray-500 text-sm">No social</span>
                        )}
                      </div>
                    </td>

                    {/* Stats */}
                    <td className="px-4 py-3">
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">Referrals:</span>
                          <span className="text-cyan-400 font-medium">{marketer.stats?.total_referrals || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">Earnings:</span>
                          <span className="text-yellow-400 font-medium">${(marketer.stats?.total_earnings_usdt || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <Badge variant={marketer.is_active ? "success" : "destructive"} className="text-xs">
                          {marketer.is_active ? "Active" : "Inactive"}
                        </Badge>
                        {marketer.is_verified && (
                          <Badge variant="outline" className="text-xs text-green-400 border-green-400">
                            Verified
                          </Badge>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(marketer)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleVerifyMarketer(marketer.user_id, !marketer.is_verified)}
                          className={marketer.is_verified ? "text-green-400" : "text-gray-400 hover:text-green-400"}
                          title={marketer.is_verified ? "Remove verification" : "Verify marketer"}
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(marketer.user_id, !marketer.is_active)}
                          className={marketer.is_active ? "text-gray-400 hover:text-red-400" : "text-red-400"}
                          title={marketer.is_active ? "Deactivate" : "Activate"}
                        >
                          {marketer.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Marketer Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              Marketer Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            </div>
          ) : marketerDetails ? (
            <div className="space-y-6">
              {/* Profile Section */}
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <h3 className="font-semibold text-white mb-3">Profile Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Name:</span>
                    <span className="text-white ml-2">
                      {marketerDetails.profile?.full_name || marketerDetails.user_account?.name || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Email:</span>
                    <span className="text-white ml-2">{marketerDetails.user_account?.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Phone:</span>
                    <span className="text-white ml-2">{marketerDetails.profile?.phone_number || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Country:</span>
                    <span className="text-white ml-2">{marketerDetails.profile?.country || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Referral Code:</span>
                    <span className="text-cyan-400 ml-2 font-mono">{marketerDetails.profile?.referral_code}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Wallet TRC20:</span>
                    <span className="text-yellow-400 ml-2 font-mono text-xs">
                      {marketerDetails.profile?.wallet_address_trc20 || 'Not set'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Social Media Section */}
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <h3 className="font-semibold text-white mb-3">Social Media</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {marketerDetails.profile?.telegram_username && (
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-blue-400" />
                      <span className="text-gray-400">Telegram:</span>
                      <a href={`https://t.me/${marketerDetails.profile.telegram_username}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                        @{marketerDetails.profile.telegram_username}
                      </a>
                    </div>
                  )}
                  {marketerDetails.profile?.whatsapp_number && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-400" />
                      <span className="text-gray-400">WhatsApp:</span>
                      <span className="text-white">{marketerDetails.profile.whatsapp_number}</span>
                    </div>
                  )}
                  {marketerDetails.profile?.facebook_url && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      <span className="text-gray-400">Facebook:</span>
                      <a href={marketerDetails.profile.facebook_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline truncate max-w-[200px]">
                        {marketerDetails.profile.facebook_url}
                      </a>
                    </div>
                  )}
                  {marketerDetails.profile?.instagram_url && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-pink-400" />
                      <span className="text-gray-400">Instagram:</span>
                      <a href={marketerDetails.profile.instagram_url} target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline truncate max-w-[200px]">
                        {marketerDetails.profile.instagram_url}
                      </a>
                    </div>
                  )}
                  {marketerDetails.profile?.youtube_url && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-red-400" />
                      <span className="text-gray-400">YouTube:</span>
                      <a href={marketerDetails.profile.youtube_url} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline truncate max-w-[200px]">
                        {marketerDetails.profile.youtube_url}
                      </a>
                    </div>
                  )}
                </div>
                {!marketerDetails.profile?.telegram_username && 
                 !marketerDetails.profile?.whatsapp_number && 
                 !marketerDetails.profile?.facebook_url && (
                  <p className="text-gray-500 text-sm">No social media accounts linked</p>
                )}
              </div>

              {/* Statistics Section */}
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <h3 className="font-semibold text-white mb-3">Statistics</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-cyan-400">{marketerDetails.summary?.total_referrals || 0}</div>
                    <div className="text-sm text-gray-400">Total Referrals</div>
                  </div>
                  <div className="text-center p-3 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-400">${(marketerDetails.summary?.total_earnings || 0).toFixed(2)}</div>
                    <div className="text-sm text-gray-400">Total Earnings</div>
                  </div>
                  <div className="text-center p-3 bg-gray-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-green-400">${(marketerDetails.summary?.total_withdrawn || 0).toFixed(2)}</div>
                    <div className="text-sm text-gray-400">Total Withdrawn</div>
                  </div>
                </div>
              </div>

              {/* Recent Referrals */}
              {marketerDetails.referrals?.length > 0 && (
                <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                  <h3 className="font-semibold text-white mb-3">Recent Referrals ({marketerDetails.referrals.length})</h3>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {marketerDetails.referrals.slice(0, 10).map((ref, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-700/30 rounded-lg text-sm">
                        <div>
                          <span className="text-white">{ref.subscriber_name || 'Unknown'}</span>
                          <span className="text-gray-400 ml-2">({ref.subscriber_email})</span>
                        </div>
                        <Badge variant={ref.status === 'active' ? 'success' : 'secondary'}>
                          {ref.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No details available</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
